import { A } from '@solidjs/router';
import { Show, createSignal } from 'solid-js';
import IconHamburger from '~/assets/icons/hamburger.svg';
import IconEmail from '~/assets/images/home/email.svg';
import IconPhone from '~/assets/images/home/phone.svg';
import { EMAIL, PHONE, FOLLOW_US, SUBMIT_A_RENTAL_APPLICATION, TENANT_SIGN_IN } from '~/assets/strings';
import { Button } from '~/components/common/buttons';
import { LocalizationButton } from '~/components/layouts/Navigation/localization/LocalizationButton';
import { IconFaceBook, IconInstagram, IconTK, IconTwitter, IconYoutube, IconLinkedin, Icon, IconX } from '~/components/ui/Icons';
import useUseCase from '~/framework/hooks/useUseCase';
import { useStyling } from '~/global-contexts/CompanyStyling';
import { useLocalization } from '~/hooks/useLocalization';
import { PresentableCompayStyle } from '~/presenters/CompanyStylePresenter';
import { CreateRentalApplicationUseCase } from '~/use-cases/rental-applications/createRentalApplicationUseCase';
import { Menu } from './Menu';

interface HeaderProps {
  companyStyling: PresentableCompayStyle | undefined;
}

const SocialMediaIcon = (props: { icon: Icon; href: string }) => {
  return (
    <A href={props.href} target="_blank" class="rounded-md p-2 text-[var(--primary-color)] hover:bg-hover-color hover:text-white">
      <props.icon class="size-[14px]" color="currentColor" />
    </A>
  );
};

export const Header = (props: HeaderProps) => {
  const { t } = useLocalization();
  const [isMenuOpen, setIsMenuOpen] = createSignal(false);
  const { execute: createRentalApplication } = useUseCase(CreateRentalApplicationUseCase);
  const { companyStyling } = useStyling();

  const onCreateRentalApplication = async () => {
    await createRentalApplication();
  };

  const navigateToTenantSignIn = () => {
    window.location.href = `/tenants`;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen());
  };

  return (
    <div class="fixed top-0 z-50 flex w-full flex-col items-center bg-white">
      <div class="hidden w-full border-b border-b-[#D5D9E080] md:block">
        <div class="mx-auto flex h-[54px] w-full max-w-container px-4 md:px-6">
          <div class="flex items-center">
            <div class="flex items-center gap-2">
              <img src={IconEmail} alt="email" />
              <span class="text-sm text-[#33333366]">{t(EMAIL)}</span>
              <Show when={props?.companyStyling?.email}>
                <a href={`mailto:${props?.companyStyling?.email}`} class="text-sm text-[#333333] hover:text-hover-color hover:underline">
                  {props?.companyStyling?.email}
                </a>
              </Show>
            </div>
            <div class="mx-5 h-[20px] w-px bg-[#3333331A]" />
            <div class="flex items-center gap-2">
              <img src={IconPhone} alt="phone" />
              <span class="text-sm text-[#33333366]">{t(PHONE)}</span>
              <Show when={props?.companyStyling?.phoneNumber}>
                <a href={`tel:${props?.companyStyling?.phoneNumber}`} class="text-sm text-[#333333] hover:text-hover-color hover:underline">
                  {props?.companyStyling?.phoneNumber}
                </a>
              </Show>
            </div>
          </div>

          <div class="ml-auto flex items-center justify-end gap-2">
            <Show when={props?.companyStyling?.socialMedia}>
              <div class="flex items-center gap-[20px]">
                <div class="text-sm text-[#33333366]">{t(FOLLOW_US)}:</div>
                <div class="flex items-center gap-3">
                  <Show when={props?.companyStyling?.facebook}>
                    <SocialMediaIcon icon={IconFaceBook} href={props?.companyStyling?.facebook ?? ''} />
                  </Show>
                  <Show when={props?.companyStyling?.instagram}>
                    <SocialMediaIcon icon={IconInstagram} href={props?.companyStyling?.instagram ?? ''} />
                  </Show>
                  <Show when={props?.companyStyling?.linkedIn}>
                    <SocialMediaIcon icon={IconLinkedin} href={props?.companyStyling?.linkedIn ?? ''} />
                  </Show>
                  <Show when={props?.companyStyling?.twitter}>
                    <SocialMediaIcon icon={IconTwitter} href={props?.companyStyling?.twitter ?? ''} />
                  </Show>
                  <Show when={props?.companyStyling?.youtube}>
                    <SocialMediaIcon icon={IconYoutube} href={props?.companyStyling?.youtube ?? ''} />
                  </Show>
                  <Show when={props?.companyStyling?.tikTok}>
                    <SocialMediaIcon icon={IconTK} href={props?.companyStyling?.tikTok ?? ''} />
                  </Show>
                </div>
              </div>
            </Show>
            <LocalizationButton />
          </div>
        </div>
      </div>

      <div class="w-full border-b border-b-[#D5D9E080]">
        <div class="mx-auto flex h-header-height w-full max-w-container items-center justify-between px-4 md:px-6">
          <div class="flex items-center gap-10">
            <Show
              when={props?.companyStyling?.logo}
              fallback={<div class="w-fit max-w-[200px] text-lg font-bold">{props?.companyStyling?.name}</div>}>
              <A href="/">
                <img src={props?.companyStyling?.logo} class="aspect-auto h-[50px] w-auto max-w-[200px]" alt="logo" />
              </A>
            </Show>
            <div class="hidden md:block">
              <Menu />
            </div>
          </div>

          <div class="hidden items-center gap-3 md:flex">
            <Button onClick={navigateToTenantSignIn} class="bg-white text-primary-color">
              {t(TENANT_SIGN_IN)}
            </Button>
            <Show when={companyStyling?.()?.canReceiveRentalApplications}>
              <Button onClick={onCreateRentalApplication}>{t(SUBMIT_A_RENTAL_APPLICATION)}</Button>
            </Show>
          </div>

          <div class="flex gap-2 md:hidden">
            <LocalizationButton />

            <button onClick={toggleMenu} class="p-2">
              {isMenuOpen() ? <IconX class="size-6" /> : <img src={IconHamburger} alt="hamburger" class="size-6" />}
            </button>
          </div>
        </div>
      </div>

      <Show when={isMenuOpen()}>
        <div class="absolute top-[72px] w-full bg-white shadow-lg md:hidden">
          <div class="mx-auto w-full max-w-container px-4 py-6">
            <Menu />
            <div class="mt-6 flex flex-col gap-4">
              <Show when={props?.companyStyling?.email}>
                <a href={`mailto:${props?.companyStyling?.email}`} class="flex items-center gap-2 text-sm">
                  <img src={IconEmail} alt="email" class="size-4" />
                  {props?.companyStyling?.email}
                </a>
              </Show>
              <Show when={props?.companyStyling?.phoneNumber}>
                <a href={`tel:${props?.companyStyling?.phoneNumber}`} class="flex items-center gap-2 text-sm">
                  <img src={IconPhone} alt="phone" class="size-4" />
                  {props?.companyStyling?.phoneNumber}
                </a>
              </Show>

              <Show when={props?.companyStyling?.socialMedia}>
                <div class="flex flex-col gap-2">
                  <span class="text-sm text-[#33333366]">{t(FOLLOW_US)}:</span>
                  <div class="flex flex-wrap gap-2">
                    <Show when={props?.companyStyling?.facebook}>
                      <SocialMediaIcon icon={IconFaceBook} href={props?.companyStyling?.facebook ?? ''} />
                    </Show>
                    <Show when={props?.companyStyling?.instagram}>
                      <SocialMediaIcon icon={IconInstagram} href={props?.companyStyling?.instagram ?? ''} />
                    </Show>
                    <Show when={props?.companyStyling?.linkedIn}>
                      <SocialMediaIcon icon={IconLinkedin} href={props?.companyStyling?.linkedIn ?? ''} />
                    </Show>
                    <Show when={props?.companyStyling?.twitter}>
                      <SocialMediaIcon icon={IconTwitter} href={props?.companyStyling?.twitter ?? ''} />
                    </Show>
                    <Show when={props?.companyStyling?.youtube}>
                      <SocialMediaIcon icon={IconYoutube} href={props?.companyStyling?.youtube ?? ''} />
                    </Show>
                    <Show when={props?.companyStyling?.tikTok}>
                      <SocialMediaIcon icon={IconTK} href={props?.companyStyling?.tikTok ?? ''} />
                    </Show>
                  </div>
                </div>
              </Show>

              <div class="flex flex-col gap-2">
                <Button onClick={navigateToTenantSignIn} class="w-full bg-white text-primary-color">
                  {t(TENANT_SIGN_IN)}
                </Button>
                <Show when={companyStyling?.()?.canReceiveRentalApplications}>
                  <Button onClick={onCreateRentalApplication} class="w-full">
                    {t(SUBMIT_A_RENTAL_APPLICATION)}
                  </Button>
                </Show>
              </div>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
